/**
 * The well as a wire lattice: a 96 x 96 sheet over +-26 kpc whose height is the potential and whose colour is its depth.
 * The topology is built once as an indexed set of line segments over 9,216 shared nodes; moving the halo slider only
 * rewrites the node heights and colours in place. Pure typed-array code, no three.js.
 */
import { wellZFromRim, phi, WELL_RIM_KPC } from './potential';

export const GRID_N = 96;
export const GRID_HALF = 26;

export interface LatticeGrid {
  /** nodes per side */
  n: number;
  nodeCount: number;
  /** x, y per node */
  xy: Float32Array;
  /** radius from the centre per node */
  radii: Float32Array;
  /** line segment endpoints, two indices per segment */
  index: Uint16Array;
}

export function buildLatticeGrid(n: number = GRID_N, half: number = GRID_HALF): LatticeGrid {
  const nodeCount = n * n;
  const xy = new Float32Array(nodeCount * 2), radii = new Float32Array(nodeCount);
  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      const k = j * n + i, x = -half + (2 * half * i) / (n - 1), y = -half + (2 * half * j) / (n - 1);
      xy[2 * k] = x; xy[2 * k + 1] = y; radii[k] = Math.hypot(x, y);
    }
  }
  const index = new Uint16Array(2 * n * (n - 1) * 2);
  let o = 0;
  for (let j = 0; j < n; j++) for (let i = 0; i < n - 1; i++) { index[o++] = j * n + i; index[o++] = j * n + i + 1; }   // rows
  for (let i = 0; i < n; i++) for (let j = 0; j < n - 1; j++) { index[o++] = j * n + i; index[o++] = (j + 1) * n + i; }   // columns
  return { n, nodeCount, xy, radii, index };
}

type Stop = readonly [number, readonly [number, number, number]];
/** gold at the shallow edge, orange midway, red in the pit */
export const DEPTH_STOPS: readonly Stop[] = [[0, [0.8, 0.58, 0.2]], [0.5, [1.0, 0.3, 0.08]], [1, [0.95, 0.05, 0.03]]];

const clamp01 = (x: number) => Math.max(0, Math.min(1, x));

/** Colour for a node at height z and radius r, written to out[o..o+2]. */
export function depthColor(z: number, r: number, out: Float32Array, o: number): void {
  const f = Math.pow(clamp01((-1.5 - z) / 26), 0.45);
  let a = DEPTH_STOPS[0], b = DEPTH_STOPS[DEPTH_STOPS.length - 1];
  for (let s = 1; s < DEPTH_STOPS.length; s++) { if (f <= DEPTH_STOPS[s][0]) { a = DEPTH_STOPS[s - 1]; b = DEPTH_STOPS[s]; break; } }
  const t = b[0] === a[0] ? 0 : clamp01((f - a[0]) / (b[0] - a[0]));
  const fade = (1 - clamp01((r - 12) / 12)) * 0.9; // the sheet dissolves toward the rim
  out[o] = (a[1][0] + (b[1][0] - a[1][0]) * t) * fade;
  out[o + 1] = (a[1][1] + (b[1][1] - a[1][1]) * t) * fade;
  out[o + 2] = (a[1][2] + (b[1][2] - a[1][2]) * t) * fade;
}

/** Rewrite node positions and colours in place for halo strength hk. */
export function fillLattice(grid: LatticeGrid, hk: number, pos: Float32Array, col: Float32Array): void {
  const phiRim = phi(WELL_RIM_KPC, hk);
  for (let k = 0; k < grid.nodeCount; k++) {
    const r = grid.radii[k], z = wellZFromRim(r, hk, phiRim);
    pos[3 * k] = grid.xy[2 * k]; pos[3 * k + 1] = grid.xy[2 * k + 1]; pos[3 * k + 2] = z;
    depthColor(z, r, col, 3 * k);
  }
}
